// Cannot-transport modal (#2139): the China Logist declares a request
// un-transportable. Terminal our-fault outcome — distinct from a customer
// REFUSED. Confirms by issuing the `cannotTransport` transition with a
// required reason (stored on the audit row).
// INVARIANT (#2139 FR-2): MUST submit key:'cannotTransport' — never 'return'.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, Modal, Textarea } from '../../../../ui/index.js';
import { useRequestsStore } from '../../store.js';
import { useTransition } from '../../queries.js';
import { errorMessage } from '../../../../api/errors.js';
import type { RequestView, UserRef } from '../../types.js';

export interface CannotTransportModalProps {
  request: RequestView;
  me: UserRef;
}

export function CannotTransportModal({ request }: CannotTransportModalProps) {
  const { t } = useTranslation(['requests', 'common']);
  const [reason, setReason] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const setModal = useRequestsStore((s) => s.setModal);
  const transitionMutation = useTransition(request.id);

  const onClose = () => setModal(null);

  async function onSubmit(): Promise<void> {
    setSubmitError(null);
    try {
      await transitionMutation.mutateAsync({
        key: 'cannotTransport',
        body: { reason: reason.trim() },
      });
      setModal(null);
    } catch (err) {
      setSubmitError(errorMessage(err));
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('requests:cannotTransportModal.title', { id: request.id })}
      width={520}
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            kind="danger"
            icon="x"
            disabled={!reason.trim() || transitionMutation.isPending}
            onClick={() => {
              void onSubmit();
            }}
          >
            {t('requests:cannotTransportModal.submit')}
          </Button>
        </>
      }
    >
      <div style={{ fontSize: 13, color: 'var(--fg-2)', marginBottom: 12 }}>
        {t('requests:cannotTransportModal.explanation')}
      </div>
      <div style={{ fontSize: 11, color: 'var(--fg-3)', marginBottom: 5 }}>
        {t('requests:cannotTransportModal.reasonLabel')}{' '}
        <span style={{ color: 'var(--status-error-fg)' }}>*</span>
      </div>
      <Textarea
        value={reason}
        onChange={setReason}
        rows={4}
        placeholder={t('requests:cannotTransportModal.reasonPlaceholder')}
      />
      {submitError && (
        <div
          style={{
            marginTop: 12,
            fontSize: 12,
            color: 'var(--status-error-fg)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Icon name="alert-triangle" size={14} />
          <span>{submitError}</span>
        </div>
      )}
    </Modal>
  );
}
